import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './Auth.css';
import { detectInitialUserSettings } from '../utils/userSettings';
import { buildAuthNavigationTarget, resolveReturnTo } from './returnTo';

// Using Firebase Auth UID as the canonical user document ID.

function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = resolveReturnTo({ location, fallback: '/home' });

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    let createdUser = null;

    try {
      const trimmedFirst = firstName.trim();
      const trimmedLast = lastName.trim();
      if (!trimmedFirst || !trimmedLast) {
        throw new Error('Please enter your first and last name.');
      }

      // 1) Create Firebase Auth user
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const user = cred.user;
      createdUser = user;

      // 2) Build user doc
      const now = serverTimestamp();
      const initialSettings = await detectInitialUserSettings();
      const profileDocBase = {
        uid: user.uid,
        id: user.uid,
        firstName: trimmedFirst,
        lastName: trimmedLast,
        email: user.email || email,
        photoPath: '',
        photoThumbPath: '',
        identities: [
          { provider: 'password', subject: user.uid }
        ],
        primaryAuthUid: user.uid,
        profileVisibility: 'public',
        createdAt: now,
        updatedAt: now,
        adminGroups: [],
        roles: { fan: true },
        subscriptions: {
          artist: { isActive: false, status: 'inactive', hasEverSubscribed: false, activatedAt: null, endedAt: null },
        },
        settings: initialSettings,
      };

      await setDoc(
        doc(db, 'users', user.uid),
        profileDocBase,
        { merge: true }
      );

      // 3) Send verification email
      await sendEmailVerification(user);

      navigate(returnTo, { replace: true });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Signup failed');
      if (createdUser?.uid) {
        try {
          await createdUser.delete();
        } catch {
          // Best-effort cleanup only
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-layout">
        <div className="auth-visual">
          <span className="tag">Artist Access</span>
          <h1>Launch your next collectible release.</h1>
          <p>
            Razzberry helps music artists launch collectibles to a verified audience.
            Apply, subscribe, and start building.
          </p>
        </div>

        <form className="auth-box" onSubmit={handleSignup}>
          <h1>Razzberry</h1>
          <h2>Create Account</h2>
          <p className="auth-subtitle">Join artists building fans with collectibles.</p>

        {error && <p className="error">{error}</p>}

        <input
          type="text"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          autoComplete="given-name"
          name="firstName"
        />

        <input
          type="text"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
          autoComplete="family-name"
          name="lastName"
        />

        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          name="email"
        />

        <input
          type="password"
          placeholder="Create Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
          name="newPassword"
        />

        <button type="submit" disabled={loading}>
          {loading ? 'Creating…' : 'Sign Up'}
        </button>

          <p className="link" onClick={() => navigate(buildAuthNavigationTarget('/signin', returnTo), { replace: true })}>
            Already have an account? Log in
          </p>
        </form>
      </div>
    </div>
  );
}

export default Signup;
